import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link, useLocation } from "react-router-dom";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { useSidebar } from "./SidebarContext";
import { MenuList } from './Menu';
import { filterSettingsSections } from '../../settings/settingsSections';
import {
  getActiveModule,
  getModuleEntryPath,
  isDashboardPath,
  isSettingsShellPath,
  getCredentialVaultNavSideFromPath,
} from './moduleNav';
import { getSubItemIconClass, getSettingsItemIconClass } from './menuIcons';
import { hasFeatureAccess } from '../../../utils/featureLock';
import { hasDocumentSubscription } from '../../../utils/documentAccess';
import { hasForumSubscription } from '../../../utils/forumAccess';
import { hasInvoiceScannerSubscription } from '../../../utils/invoiceScannerAccess';
import { accessControlService } from '../../../services/accessControlService';
import { submenuAllowedByMyAccess } from '../../../utils/routeAccessConfig';
import {
  hasAdminRole,
  isCorporateAdmin,
  canAccessBranches,
  canAccessStaff,
  canAccessVehicles,
  canAccessDesignations,
  canAccessUsers,
  canAccessWorksheet,
  canAccessDepartments,
  canAccessSubDepartments,
  getCurrentUserRole,
  canDo,
} from '../../../utils/roleAccess';
import './sidebarNav.css';
import SidebarProfile from './SidebarProfile';

const normalizeRole = (role) => {
  if (!role) return '';
  return role.toLowerCase().replace(/\s+/g, '_').trim();
};

const SETTINGS_TITLE = 'Settings';
const subscriptionFeatureRequiredPath = '/subscription-feature-required';

const SideBar = () => {
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  const { isOpen, closeSidebar, openSidebar } = useSidebar();
  const location = useLocation();
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [hasBranchAccess, setHasBranchAccess] = useState(false);
  const [hasDepartmentAccess, setHasDepartmentAccess] = useState(false);
  const [hasWorksheetAccess, setHasWorksheetAccess] = useState(false);
  const [hasDocumentAccess, setHasDocumentAccess] = useState(false);
  const [hasForumAccess, setHasForumAccess] = useState(false);
  const [hasInvoiceScannerAccess, setHasInvoiceScannerAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [userPlanFeaturesState, setUserPlanFeaturesState] = useState([]);
  const [menuAccessKeys, setMenuAccessKeys] = useState(null);
  const [menuAccessRoutePaths, setMenuAccessRoutePaths] = useState(null);
  const [menuAccessFetchResolved, setMenuAccessFetchResolved] = useState(false);
  const [hideOnScroll, setHideOnScroll] = useState(true);
  const [openSections, setOpenSections] = useState({});

  const currentPath = location.pathname;
  const onDashboard = isDashboardPath(currentPath);
  const onSettingsShell = isSettingsShellPath(currentPath);
  const showMainModules = onDashboard || onSettingsShell;
  const activeModuleInfo = getActiveModule(currentPath, location.search, location.state);

  const loadUserPlanFeatures = () => {
    try {
      const featuresStr = localStorage.getItem('userPlanFeatures');
      if (featuresStr) {
        const parsed = JSON.parse(featuresStr);
        let features = [];
        if (Array.isArray(parsed)) {
          features = parsed.map(f => {
            if (typeof f === 'string') return f;
            if (f.feature) return f.feature;
            if (f.feature_key) return f.feature_key;
            if (f.key) return f.key;
            return null;
          }).filter(f => f !== null);
        }
        setUserPlanFeaturesState(features);
      } else {
        setUserPlanFeaturesState([]);
      }
    } catch (e) {
      console.error('Error parsing userPlanFeatures from localStorage:', e);
      setUserPlanFeaturesState([]);
    }
  };

  useEffect(() => {
    setUserRole(getCurrentUserRole());
  }, []);

  const fetchMenuAccess = async () => {
    try {
      const res = await accessControlService.getMyAccess();
      const keys = res?.data?.data?.menuKeys;
      if (res?.data?.success) {
        setMenuAccessKeys(Array.isArray(keys) ? keys : []);
        const rp = res?.data?.data?.routePaths;
        setMenuAccessRoutePaths(Array.isArray(rp) ? rp : null);
      } else {
        setMenuAccessKeys(null);
        setMenuAccessRoutePaths(null);
      }
    } catch (err) {
      setMenuAccessKeys(null);
      setMenuAccessRoutePaths(null);
    } finally {
      setMenuAccessFetchResolved(true);
    }
  };

  useEffect(() => {
    fetchMenuAccess();
  }, []);

  useEffect(() => {
    const onAccessUpdated = () => fetchMenuAccess();
    window.addEventListener('menu-access-updated', onAccessUpdated);
    return () => window.removeEventListener('menu-access-updated', onAccessUpdated);
  }, []);

  useEffect(() => {
    loadUserPlanFeatures();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const mainWrapper = document.querySelector("#main-wrapper");
    if (mainWrapper) {
      if (isOpen) {
        mainWrapper.classList.add("menu-toggle");
      } else {
        mainWrapper.classList.remove("menu-toggle");
      }
    }
  }, [isOpen]);

  // Module pages default to collapsed (icon rail) on desktop; dashboard/settings stay expanded.
  // Mobile keeps the overlay closed until the user opens it.
  useEffect(() => {
    if (isMobile) {
      closeSidebar();
      return;
    }
    if (!showMainModules) {
      openSidebar();
    } else {
      closeSidebar();
    }
  }, [showMainModules, isMobile, openSidebar, closeSidebar]);

  // Keep overlay layouts aware of dashboard so sidebar can stay visible
  useEffect(() => {
    const mainWrapper = document.querySelector("#main-wrapper");
    if (!mainWrapper) return undefined;
    if (onDashboard) {
      mainWrapper.classList.add("on-business-dashboard");
    } else {
      mainWrapper.classList.remove("on-business-dashboard");
    }
    return () => mainWrapper.classList.remove("on-business-dashboard");
  }, [onDashboard]);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll]
  );

  const isPathActive = (menuPath) => {
    if (!menuPath) return false;
    const normalizedMenuPath = menuPath.endsWith('/') && menuPath.length > 1
      ? menuPath.slice(0, -1)
      : menuPath;
    const normalizedCurrentPath = currentPath.endsWith('/') && currentPath.length > 1
      ? currentPath.slice(0, -1)
      : currentPath;

    const menuPathToCheck = normalizedMenuPath.startsWith('/')
      ? normalizedMenuPath
      : '/' + normalizedMenuPath;

    const menuPathParts = menuPathToCheck.split('/').filter(p => p);
    const currentPathParts = normalizedCurrentPath.split('/').filter(p => p);
    const isCreateSegment = (segment = '') => /^(add|new)/i.test(segment);

    if (normalizedCurrentPath === menuPathToCheck) {
      return true;
    }
    if (normalizedCurrentPath.startsWith(menuPathToCheck + '/')) {
      const childSeg = normalizedCurrentPath.slice(menuPathToCheck.length + 1).split('/')[0] || '';
      // /settings/flowdoRoles/new should highlight Add Role, not the roles list.
      if (!isCreateSegment(childSeg)) return true;
    }

    if (menuPathParts[0] === 'staff' && currentPathParts[0] === 'staff') return true;
    if (
      (menuPathParts[0] === 'vehicle' || menuPathToCheck.includes('vehicle')) &&
      currentPathParts[0] === 'vehicle'
    ) {
      return true;
    }

    const menuIsVault = menuPathParts[0] === 'credential-vault';
    if (menuIsVault && menuPathToCheck.includes('credential-vault')) {
      if (currentPathParts[0] !== 'credential-vault') return false;
      const onVaultSettings = currentPathParts[1] === 'vaultSettings';
      const subItemIsVaultSettings =
        menuPathParts[1] === 'vaultSettings' || menuPathToCheck.includes('/vaultSettings');
      if (subItemIsVaultSettings) return onVaultSettings;

      const navSide = getCredentialVaultNavSideFromPath(
        normalizedCurrentPath,
        location.search,
        location.state
      );
      const listSeg = menuPathParts[1];
      if (listSeg === 'credentialVaultList') return navSide === 'corporate';
      if (listSeg === 'personalVaultList') return navSide === 'personal';
      return false;
    }

    // Project routes under /task — keep list vs create distinct (before semantic add/edit match).
    if (
      menuPathParts[0] === 'task' &&
      menuPathParts[1]?.toLowerCase().includes('project') &&
      currentPathParts[0] === 'task' &&
      currentPathParts[1]?.toLowerCase().includes('project')
    ) {
      const menuSeg = menuPathParts[1]?.toLowerCase() || '';
      const currentSeg = currentPathParts[1]?.toLowerCase() || '';
      if (menuSeg === 'addproject') return currentSeg === 'addproject';
      if (menuSeg === 'projectdashboard') return currentSeg === 'projectdashboard';
      if (menuSeg === 'projecttasklist') return currentSeg === 'projecttasklist';
      if (menuSeg === 'projectlist') {
        return (
          currentSeg === 'projectlist' ||
          currentSeg === 'projectdetails' ||
          currentSeg === 'editproject'
        );
      }
      return menuSeg === currentSeg;
    }

    // Semantic match for settings add/edit routes.
    // Keep dedicated add pages (Add User / Add Role) from lighting the list item.
    if (currentPathParts.length >= 2 && menuPathParts.length >= 2) {
      if (currentPathParts[0] === menuPathParts[0]) {
        const currentSeg = currentPathParts[1]?.toLowerCase() || '';
        const menuSeg = menuPathParts[1]?.toLowerCase() || '';
        const currentIsCreate = isCreateSegment(currentSeg) || isCreateSegment(currentPathParts[2]);
        const menuIsCreate = isCreateSegment(menuSeg) || isCreateSegment(menuPathParts[2]);
        if (
          currentPathParts[0] === 'settings' &&
          currentIsCreate !== menuIsCreate
        ) {
          return false;
        }
        const currentKey = currentSeg.replace(/^(add|edit)/, '').replace(/list$/, '') || '';
        const subItemKey = menuSeg.replace(/list$/, '') || '';
        if (currentKey && subItemKey && currentKey === subItemKey) return true;
      }
    }

    if (
      menuPathToCheck.includes('/task/performance') &&
      currentPathParts[0] === 'task' &&
      currentPathParts[1] === 'performance'
    ) {
      return true;
    }

    if (currentPathParts[0] === 'forum') {
      if (menuPathParts[1] === 'dashboard') {
        return currentPathParts[1] === 'dashboard';
      }
      if (
        (menuPathParts[1] === 'list' || (menuPathParts.includes('list'))) &&
        (currentPathParts[1] === 'list' || currentPathParts[1] === 'detail')
      ) {
        return menuPathParts[1] === 'list';
      }
      if (
        menuPathParts[1] === 'create' &&
        (currentPathParts[1] === 'create' || currentPathParts[1] === 'edit')
      ) {
        return true;
      }
    }

    if (currentPathParts[0] === 'documents') {
      const action = currentPathParts[1]?.toLowerCase() || '';
      if (['upload', 'details', 'folder'].includes(action) && menuPathToCheck.includes('mydocuments')) {
        return true;
      }
    }

    return false;
  };

  const handleMenuItemClick = () => {
    if (isMobile && isOpen) closeSidebar();
  };

  const recheckFeatureAccess = async () => {
    try {
      const [projectAccess, branchAccess, departmentAccess, worksheetAccess] = await Promise.all([
        hasFeatureAccess('project'),
        hasFeatureAccess('branch'),
        hasFeatureAccess('department'),
        hasFeatureAccess('worksheet')
      ]);
      setHasProjectAccess(projectAccess);
      setHasBranchAccess(branchAccess);
      setHasDepartmentAccess(departmentAccess);
      setHasWorksheetAccess(worksheetAccess);
    } catch (error) {
      console.error('Error checking feature access:', error);
      setHasProjectAccess(false);
      setHasBranchAccess(false);
      setHasDepartmentAccess(false);
      setHasWorksheetAccess(false);
    }
  };

  // Check document and forum subscription access on mount and location change
  useEffect(() => {
    const checkModuleAccess = async () => {
      try {
        const { clearDocumentSubscriptionCache } = await import('../../../utils/documentAccess');
        const { clearForumSubscriptionCache } = await import('../../../utils/forumAccess');
        const { clearInvoiceScannerSubscriptionCache } = await import('../../../utils/invoiceScannerAccess');
        clearDocumentSubscriptionCache();
        clearForumSubscriptionCache();
        clearInvoiceScannerSubscriptionCache();
        const [docAccess, forumAccess, invoiceScannerAccess] = await Promise.all([
          hasDocumentSubscription(),
          hasForumSubscription(),
          hasInvoiceScannerSubscription(),
        ]);
        setHasDocumentAccess(docAccess);
        setHasForumAccess(forumAccess);
        setHasInvoiceScannerAccess(invoiceScannerAccess);
      } catch (error) {
        console.error('Error checking module subscriptions:', error);
        setHasDocumentAccess(false);
        setHasForumAccess(false);
        setHasInvoiceScannerAccess(false);
      }
    };
    checkModuleAccess();
  }, [location.pathname]);

  // Listen for user changes (login/logout) to refresh document/forum access
  useEffect(() => {
    const handleUserChange = async () => {
      try {
        const { clearDocumentSubscriptionCache } = await import('../../../utils/documentAccess');
        const { clearForumSubscriptionCache } = await import('../../../utils/forumAccess');
        const { clearInvoiceScannerSubscriptionCache } = await import('../../../utils/invoiceScannerAccess');
        clearDocumentSubscriptionCache();
        clearForumSubscriptionCache();
        clearInvoiceScannerSubscriptionCache();
        const [docAccess, forumAccess, invoiceScannerAccess] = await Promise.all([
          hasDocumentSubscription(),
          hasForumSubscription(),
          hasInvoiceScannerSubscription(),
        ]);
        setHasDocumentAccess(docAccess);
        setHasForumAccess(forumAccess);
        setHasInvoiceScannerAccess(invoiceScannerAccess);
      } catch (error) {
        console.error('Error refreshing module subscriptions on user change:', error);
        setHasDocumentAccess(false);
        setHasForumAccess(false);
        setHasInvoiceScannerAccess(false);
      }
    };

    const handleLogout = () => {
      setHasDocumentAccess(false);
      setHasForumAccess(false);
      setHasInvoiceScannerAccess(false);
    };

    const handleStorageChange = (e) => {
      if (e.key === 'logout') handleLogout();
      else if (e.key === 'user' && e.newValue) handleUserChange();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-subscription-updated', handleUserChange);
    window.addEventListener('user-logged-in', handleUserChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-subscription-updated', handleUserChange);
      window.removeEventListener('user-logged-in', handleUserChange);
    };
  }, []);

  useEffect(() => {
    recheckFeatureAccess();
  }, []);

  useEffect(() => {
    const onSubscriptionUpdated = async () => {
      loadUserPlanFeatures();
      recheckFeatureAccess();
      try {
        const { clearInvoiceScannerSubscriptionCache } = await import('../../../utils/invoiceScannerAccess');
        clearInvoiceScannerSubscriptionCache();
        const [docAccess, forumAccess, invoiceScannerAccess] = await Promise.all([
          hasDocumentSubscription(),
          hasForumSubscription(),
          hasInvoiceScannerSubscription(),
        ]);
        setHasDocumentAccess(docAccess);
        setHasForumAccess(forumAccess);
        setHasInvoiceScannerAccess(invoiceScannerAccess);
      } catch (error) {
        console.error('Error refreshing module subscriptions:', error);
        setHasDocumentAccess(false);
        setHasForumAccess(false);
        setHasInvoiceScannerAccess(false);
      }
    };
    window.addEventListener('user-subscription-updated', onSubscriptionUpdated);
    return () => window.removeEventListener('user-subscription-updated', onSubscriptionUpdated);
  }, []);

  const canAccessSubItem = (subItem) => {
    if (subItem.publicAccess) return true;
    if (subItem.section && subItem.requiredAction && !canDo(subItem.section, subItem.requiredAction)) {
      return false;
    }
    if (menuAccessKeys !== null && Array.isArray(menuAccessKeys)) {
      return submenuAllowedByMyAccess(menuAccessKeys, menuAccessRoutePaths, subItem);
    }
    if (menuAccessFetchResolved && menuAccessKeys === null && subItem.menu_key) return false;
    if (!subItem.role) return true;
    const role = normalizeRole(subItem.role);
    const currentRole = normalizeRole(userRole || '');
    if (subItem.restricted_roles?.some(r => currentRole === normalizeRole(r))) return false;
    if (role === 'corporate_admin') return isCorporateAdmin(userRole);
    if (role === 'admin') return hasAdminRole(userRole);
    return currentRole === role;
  };

  const passesLegacyRoleRouteChecks = (subItem) => {
    if (menuAccessKeys !== null) return true;
    if (subItem.to === 'settings/userlist' && !canAccessUsers(userRole)) return false;
    if (subItem.to === 'staff/staffList' && !canAccessStaff(userRole)) return false;
    if (subItem.to === 'settings/branchList' && !canAccessBranches(userRole)) return false;
    if (subItem.to === '/vehicle/vehicleList' && !canAccessVehicles(userRole)) return false;
    if (subItem.to === 'settings/designationList' && !canAccessDesignations(userRole)) return false;
    if (subItem.to === '/task/worksheet' && !canAccessWorksheet(userRole)) return false;
    if (subItem.to === 'settings/departmentList' && !canAccessDepartments(userRole)) return false;
    if (subItem.to === 'settings/subDepartmentList' && !canAccessSubDepartments(userRole)) return false;
    return true;
  };

  const isModuleSubscriptionLocked = (menuItem) => {
    if (!menuItem) return false;
    if (menuItem.title === 'Documents') return !hasDocumentAccess;
    if (menuItem.title === 'Discussion Forum' || menuItem.menu_key === 'forum') {
      return !hasForumAccess;
    }
    if (menuItem.title === 'Snap Bill' || menuItem.menu_key === 'invoice_scanner') {
      return !hasInvoiceScannerAccess;
    }
    // Projects is a sidebar module only — access still comes from the Task plan `project` feature.
    if (menuItem.title === 'Projects' || menuItem.menu_key === 'projects') {
      return !hasProjectAccess;
    }
    return false;
  };

  const isSubItemFeatureLocked = (subItem, isModuleLocked) => {
    if (subItem.publicAccess) return false;
    if (isModuleLocked) return true;
    if (subItem.feature_key === 'project') return !hasProjectAccess;
    if (subItem.feature_key === 'branch') return !hasBranchAccess;
    if (subItem.feature_key === 'department') return !hasDepartmentAccess;
    if (subItem.feature_key === 'worksheet') return !hasWorksheetAccess;
    if (subItem.feature_key === 'invoice_scanner') return !hasInvoiceScannerAccess;
    if (subItem.feature_key) return !userPlanFeaturesState.includes(subItem.feature_key);
    return false;
  };

  const getVisibleContent = (menuItem) => {
    if (!menuItem.content) return [];
    return menuItem.content.filter(
      (subItem) => canAccessSubItem(subItem) && passesLegacyRoleRouteChecks(subItem)
    );
  };

  const getLockedRedirect = (subItem, menuItem) => {
    let module = 'task';
    if (subItem.feature_key === 'document' || menuItem?.title === 'Documents') {
      module = 'document';
    } else if (
      subItem.feature_key === 'discussion_forum' ||
      menuItem?.title === 'Discussion Forum' ||
      menuItem?.menu_key === 'forum'
    ) {
      module = 'discussion_forum';
    } else if (subItem.feature_key === 'invoice_scanner') {
      module = 'invoice_scanner';
    } else if (
      subItem.feature_key === 'project' ||
      menuItem?.menu_key === 'projects' ||
      menuItem?.title === 'Projects'
    ) {
      // Projects still uses Task subscription / feature gating.
      module = 'task';
    }
    return `${subscriptionFeatureRequiredPath}?module=${module}`;
  };

  const normalizeTo = (to) => {
    if (!to) return '#';
    return to.startsWith('/') ? to : `/${to}`;
  };

  const renderSubItemLink = (subItem, menuItem, key) => {
    const moduleLocked = isModuleSubscriptionLocked(menuItem);
    const locked = isSubItemFeatureLocked(subItem, moduleLocked);
    const to = locked ? getLockedRedirect(subItem, menuItem) : normalizeTo(subItem.to);
    const active = !locked && isPathActive(subItem.to);
    const iconClass = getSubItemIconClass(subItem);

    return (
      <li key={key} className={active ? 'mm-active' : ''}>
        <Link
          to={to}
          className={active ? 'mm-active' : ''}
          onClick={handleMenuItemClick}
          title={locked ? 'This feature is not available in your current plan.' : subItem.title}
        >
          {subItem.iconStyle || <i className={iconClass} aria-hidden="true" />}
          <span className="nav-text">
            {subItem.title}
            {locked && <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }} />}
          </span>
        </Link>
      </li>
    );
  };

  const renderDashboardModules = () => {
    const modules = MenuList.filter((m) => m.title !== SETTINGS_TITLE);

    const renderModuleRow = (data) => {
      const visibleContent = getVisibleContent(data);
      if (data.content?.length > 0 && visibleContent.length === 0) return null;

      const moduleLocked = isModuleSubscriptionLocked(data);
      const entryItem = visibleContent.find((item) => item.to && item.to !== '#');
      const entryLocked =
        moduleLocked ||
        (entryItem ? isSubItemFeatureLocked(entryItem, moduleLocked) : false);
      const entryPath = getModuleEntryPath(data, visibleContent);
      const to = entryLocked && entryItem
        ? getLockedRedirect(entryItem, data)
        : (entryPath || '#');
      const isActive = activeModuleInfo?.menuTitle === data.title;

      return (
        <li
          key={data.title}
          className={`settings-sidebar-flyout-anchor${isActive ? ' mm-active' : ''}`}
        >
          <Link
            to={to}
            onClick={handleMenuItemClick}
            title={
              entryLocked
                ? 'This module is not available in your current subscription plan.'
                : data.title
            }
          >
            {data.iconStyle}
            <span className="nav-text">
              {data.title}
              {entryLocked && (
                <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }} />
              )}
            </span>
          </Link>
          {visibleContent.length > 0 ? (
            <div className="settings-sidebar-flyout" role="menu" aria-label={data.title}>
              <div className="settings-sidebar-flyout__title">{data.title}</div>
              <ul className="settings-sidebar-flyout__items">
                {visibleContent.map((subItem, index) => {
                  const subLocked = isSubItemFeatureLocked(subItem, moduleLocked);
                  const subTo = subLocked
                    ? getLockedRedirect(subItem, data)
                    : normalizeTo(subItem.to);
                  const subActive = !subLocked && isPathActive(subItem.to);
                  return (
                    <li
                      key={`${data.title}-${subItem.title}-${index}`}
                      className={subActive ? 'mm-active' : ''}
                    >
                      <Link
                        to={subTo}
                        className={subActive ? 'mm-active' : ''}
                        onClick={handleMenuItemClick}
                        role="menuitem"
                        title={
                          subLocked
                            ? 'This feature is not available in your current plan.'
                            : subItem.title
                        }
                      >
                        {subItem.iconStyle || (
                          <i className={getSubItemIconClass(subItem)} aria-hidden="true" />
                        )}
                        <span className="nav-text">
                          {subItem.title}
                          {subLocked && (
                            <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }} />
                          )}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </li>
      );
    };

    return (
      <div className="sidebar-dashboard-menus">
        <ul className="metismenu" id="menu">
          {modules.map(renderModuleRow)}
        </ul>
      </div>
    );
  };

  const toggleSection = useCallback((id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const settingsSectionOptions = useMemo(
    () => ({
      featureAvailability: { invoice_scanner: hasInvoiceScannerAccess },
    }),
    [hasInvoiceScannerAccess]
  );

  // Auto-open the section that contains the active path on settings pages
  useEffect(() => {
    if (!onSettingsShell) return;
    const sections = filterSettingsSections(userRole, menuAccessKeys, settingsSectionOptions);
    sections.forEach((section) => {
      const hasActive = section.items.some((item) => isPathActive(item.to));
      if (hasActive) {
        setOpenSections((prev) => ({ ...prev, [section.id]: true }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, onSettingsShell, userRole, menuAccessKeys, settingsSectionOptions]);

  const renderSettingsNav = () => {
    const sections = filterSettingsSections(userRole, menuAccessKeys, settingsSectionOptions);
    const onSettingsLanding = currentPath === '/settings' || currentPath === '/settings/';

    const renderSettingsItemLink = (item) => {
      const active = isPathActive(item.to);
      return (
        <Link
          to={item.to}
          className={active ? 'mm-active' : ''}
          onClick={handleMenuItemClick}
          role="menuitem"
        >
          <i className={getSettingsItemIconClass(item)} aria-hidden="true" />
          <span className="nav-text">{item.label}</span>
        </Link>
      );
    };

    return (
      <div className="settings-sidebar-nav">
        <div className="settings-sidebar-nav__header settings-sidebar-flyout-anchor">
          {onSettingsLanding ? (
            <Link to="/dashboard" className="settings-sidebar-nav__back settings-sidebar-nav__close" onClick={handleMenuItemClick}>
              <i className="fas fa-times" aria-hidden="true" />
              <span>Close Settings</span>
            </Link>
          ) : (
            <Link to="/settings" className="settings-sidebar-nav__back" onClick={handleMenuItemClick}>
              <i className="fas fa-arrow-left" aria-hidden="true" />
              <span>Settings</span>
            </Link>
          )}
          <div className="settings-sidebar-flyout settings-sidebar-flyout--label" role="tooltip">
            {onSettingsLanding ? 'Close Settings' : 'Settings'}
          </div>
        </div>
        <ul className="settings-sidebar-sections">
          {sections.map((section) => {
            const isExpanded = !!openSections[section.id];
            const hasActive = section.items.some((item) => isPathActive(item.to));

            return (
              <li
                key={section.id}
                className={`settings-sidebar-section settings-sidebar-flyout-anchor${hasActive ? ' settings-sidebar-section--active' : ''}${isExpanded ? ' settings-sidebar-section--open' : ''}`}
              >
                <button
                  type="button"
                  className={`settings-sidebar-section__toggle${isExpanded ? ' is-open' : ''}`}
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={isExpanded}
                  aria-haspopup="menu"
                >
                  <i className={section.icon} aria-hidden="true" />
                  <span className="nav-text">{section.title}</span>
                  <i className="fas fa-chevron-right settings-sidebar-section__chevron" aria-hidden="true" />
                </button>
                <div className="settings-sidebar-flyout" role="menu" aria-label={section.title}>
                  <div className="settings-sidebar-flyout__title">{section.title}</div>
                  <ul className="settings-sidebar-flyout__items">
                    {section.items.map((item) => (
                      <li key={item.id} className={isPathActive(item.to) ? 'mm-active' : ''}>
                        {renderSettingsItemLink(item)}
                      </li>
                    ))}
                  </ul>
                </div>
                {isExpanded && (
                  <ul className="settings-sidebar-section__items">
                    {section.items.map((item) => (
                      <li key={item.id} className={isPathActive(item.to) ? 'mm-active' : ''}>
                        {renderSettingsItemLink(item)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderModuleSubItems = () => {
    const module = activeModuleInfo?.module;
    if (!module) {
      // Fallback: show all modules if path doesn't map (e.g. credits, subscription)
      return renderDashboardModules();
    }

    const visibleContent = getVisibleContent(module);

    return (
      <ul className="metismenu" id="menu">
        {visibleContent.map((subItem, index) =>
          renderSubItemLink(subItem, module, `${module.title}-${index}`)
        )}
      </ul>
    );
  };

  return (
    <div
      className={`dlabnav ${iconHover} ${
        sidebarposition.value === "fixed" &&
        sidebarLayout.value === "horizontal" &&
        headerposition.value === "static"
          ? hideOnScroll > 120
            ? "fixed"
            : ""
          : ""
      } ${isOpen ? 'sidebar-open' : 'sidebar-closed'}${showMainModules ? ' dlabnav--dashboard' : ''}`}
    >
      <PerfectScrollbar className="dlabnav-scroll">
        <div className="sidebar-scroll-inner">
          {onSettingsShell
            ? renderSettingsNav()
            : onDashboard
              ? renderDashboardModules()
              : renderModuleSubItems()}
        </div>
      </PerfectScrollbar>
      <SidebarProfile />
    </div>
  );
};

export default SideBar;
